import { useEffect, useMemo, useRef, useState } from "react";
import {
  FOOD_OPTION_KEYS,
  RSVP_OPTION_LABELS,
  getEnabledFoodOptions,
  normalizeRsvpDetails,
  normalizeRsvpOptions,
} from "../../Utils/rsvpOptions";
import "./RsvpAdditionalOptions.css";

const Choice = ({ checked, disabled, label, onChange, type = "checkbox", name }) => (
  <label className={`rsvp-choice ${checked ? "rsvp-choice--selected" : ""} ${disabled ? "rsvp-choice--disabled" : ""}`}>
    <input type={type} name={name} checked={checked} disabled={disabled} onChange={onChange} />
    <span>{label}</span>
  </label>
);

const AnimatedNumber = ({ value, as: Tag = "strong", className = "", ariaLabel }) => {
  const previousValue = useRef(value);
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    if (previousValue.current === value) return;
    previousValue.current = value;
    setAnimationKey((current) => current + 1);
  }, [value]);

  return (
    <Tag
      key={animationKey}
      className={`${className} ${animationKey > 0 ? "rsvp-number--updated" : ""}`.trim()}
      aria-label={ariaLabel}
    >
      {value}
    </Tag>
  );
};

const AggregateCounter = ({ label, value, onIncrease, onDecrease, increaseDisabled, decreaseDisabled }) => (
  <div className="rsvp-aggregate__counter">
    <span>{label}</span>
    <div className="rsvp-extra__stepper">
      <button type="button" disabled={increaseDisabled} onClick={onIncrease} aria-label={`הוספת ${label}`}>+</button>
      <AnimatedNumber as="output" value={value} ariaLabel={`כמות ${label}`} />
      <button type="button" disabled={decreaseDisabled} onClick={onDecrease} aria-label={`הפחתת ${label}`}>−</button>
    </div>
  </div>
);

const RsvpAdditionalOptions = ({
  options: optionsValue,
  partySize,
  value,
  onChange,
  disabled = false,
  alwaysShowFood = false,
  compact = false,
  showTransportation = true,
  showAttendees = true,
  attendeeIndex = null,
  onFoodInteraction = null,
  regularMealIsDefault = false,
  aggregateAttendees = false,
  validationAttempt = 0,
}) => {
  const options = useMemo(() => normalizeRsvpOptions(optionsValue), [optionsValue]);
  const details = useMemo(() => normalizeRsvpDetails(value, partySize), [value, partySize]);
  const enabledFood = useMemo(() => getEnabledFoodOptions(options), [options]);
  const hasSavedFood = details.attendees.some((attendee) => FOOD_OPTION_KEYS.some((key) => attendee[key]));
  const [foodOpen, setFoodOpen] = useState(alwaysShowFood || hasSavedFood);
  const genderDropdownRef = useRef(null);

  useEffect(() => {
    if (alwaysShowFood || hasSavedFood) setFoodOpen(true);
  }, [alwaysShowFood, hasSavedFood]);

  const updateDetails = (next) => onChange(normalizeRsvpDetails(next, partySize));
  const updateAttendee = (index, patch) => {
    const attendees = details.attendees.map((attendee, attendeeIndex) =>
      attendeeIndex === index ? { ...attendee, ...patch } : attendee
    );
    updateDetails({ ...details, attendees });
  };
  const updateAttendees = (attendees) => updateDetails({ ...details, attendees });

  const adjustGender = (targetGender, direction) => {
    const attendees = details.attendees.map((attendee) => ({ ...attendee }));
    if (direction > 0) {
      const sourceIndex = attendees.findIndex((attendee) => attendee.gender === "") !== -1
        ? attendees.findIndex((attendee) => attendee.gender === "")
        : attendees.findIndex((attendee) => attendee.gender !== targetGender);
      if (sourceIndex === -1) return;
      attendees[sourceIndex].gender = targetGender;
    } else {
      const sourceIndex = attendees.map((attendee) => attendee.gender).lastIndexOf(targetGender);
      if (sourceIndex === -1) return;
      attendees[sourceIndex].gender = targetGender === "male" ? "female" : "male";
    }
    updateAttendees(attendees);
  };

  const getMealProfile = (attendee) => ({
    childMeal: options.childMeal.enabled && attendee.childMeal === true,
    ...Object.fromEntries(enabledFood.map((key) => [key, attendee[key] === true])),
  });
  const getMealKey = (profile) => [
    options.childMeal.enabled && profile.childMeal ? "child" : "adult",
    ...enabledFood.filter((key) => profile[key]),
  ].join("|");
  const getMealLabel = (profile) => [
    ...(options.childMeal.enabled && profile.childMeal ? ["מנות ילדים"] : []),
    ...(enabledFood.length > 0 ? (() => {
      const labels = enabledFood.filter((key) => profile[key]).map((key) => RSVP_OPTION_LABELS[key]);
      return labels.length > 0 ? labels : ["מנה רגילה"];
    })() : []),
  ].join(" + ");
  const defaultMealProfile = {
    childMeal: false,
    ...Object.fromEntries(enabledFood.map((key) => [key, false])),
  };
  const defaultMealKey = getMealKey(defaultMealProfile);
  const selectedMealGroups = (() => {
    const groups = new Map();
    details.attendees.forEach((attendee, index) => {
      const profile = getMealProfile(attendee);
      const key = getMealKey(profile);
      if (!groups.has(key)) groups.set(key, { key, profile, label: getMealLabel(profile), attendeeIndexes: [] });
      groups.get(key).attendeeIndexes.push(index);
    });
    return groups;
  })();
  const dietaryProfiles = Array.from({ length: 2 ** enabledFood.length }, (_, mask) => ({
    childMeal: false,
    ...Object.fromEntries(enabledFood.map((key, index) => [key, Boolean(mask & (1 << index))])),
  })).filter((profile) => !(profile.vegetarian && profile.vegan));
  const mealGroups = [false, ...(options.childMeal.enabled ? [true] : [])].flatMap((childMeal) =>
    dietaryProfiles.map((dietaryProfile) => {
      const profile = { ...dietaryProfile, childMeal };
      const key = getMealKey(profile);
      return {
        key,
        profile,
        label: getMealLabel(profile),
        attendeeIndexes: selectedMealGroups.get(key)?.attendeeIndexes || [],
      };
    })
  );
  const getMealSummaryLabel = (group) => {
    if (group.key === defaultMealKey) return options.childMeal.enabled ? "מנות מבוגרים רגילות" : "מנות רגילות";
    const foodLabels = {
      vegetarian: "צמחוניות",
      vegan: "טבעוניות",
      glattKosher: "גלאט כשר",
      glutenFree: "ללא גלוטן",
    };
    const selectedFoodLabels = enabledFood.filter((key) => group.profile[key]).map((key) => foodLabels[key]);
    if (group.profile.childMeal && selectedFoodLabels.length === 0) return "מנות ילדים";
    const mealType = options.childMeal.enabled
      ? (group.profile.childMeal ? "מנות ילדים" : "מנות מבוגרים")
      : "מנות";
    return [
      `${mealType} ${selectedFoodLabels[0]}`,
      ...selectedFoodLabels.slice(1),
    ].join(" + ");
  };
  const applyMealProfile = (attendee, profile) => ({
    ...attendee,
    ...(options.childMeal.enabled ? { childMeal: profile.childMeal === true } : {}),
    ...Object.fromEntries(enabledFood.map((key) => [key, profile[key] === true])),
    ...(enabledFood.length > 0 ? {
      foodAnswered: true,
      answeredFoodOptions: Array.from(new Set([...attendee.answeredFoodOptions, ...enabledFood])),
    } : {}),
  });

  const adjustMealGroup = (group, direction) => {
    if (group.key === defaultMealKey) return;
    const attendees = details.attendees.map((attendee) => ({ ...attendee }));
    if (direction > 0) {
      const donorGroup = selectedMealGroups.get(defaultMealKey);
      const donorIndex = donorGroup?.attendeeIndexes[donorGroup.attendeeIndexes.length - 1];
      if (typeof donorIndex !== "number") return;
      attendees[donorIndex] = applyMealProfile(attendees[donorIndex], group.profile);
    } else {
      const sourceIndex = group.attendeeIndexes[group.attendeeIndexes.length - 1];
      if (typeof sourceIndex !== "number") return;
      attendees[sourceIndex] = applyMealProfile(attendees[sourceIndex], defaultMealProfile);
    }
    onFoodInteraction?.();
    updateAttendees(attendees);
  };

  const maleCount = details.attendees.filter((attendee) => attendee.gender === "male").length;
  const femaleCount = details.attendees.filter((attendee) => attendee.gender === "female").length;
  const unassignedGenderCount = partySize - maleCount - femaleCount;

  useEffect(() => {
    if (validationAttempt > 0 && unassignedGenderCount > 0 && genderDropdownRef.current) {
      genderDropdownRef.current.open = true;
      if (window.matchMedia("(max-width: 575px)").matches) {
        window.requestAnimationFrame(() => {
          genderDropdownRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          window.setTimeout(() => genderDropdownRef.current?.querySelector("summary")?.focus({ preventScroll: true }), 350);
        });
      }
    }
  }, [validationAttempt, unassignedGenderCount]);

  const showAttendeeCards = showAttendees && !aggregateAttendees && (
    options.childMeal.enabled || options.gender.enabled || (enabledFood.length > 0 && foodOpen)
  );
  const visibleAttendees = attendeeIndex === null
    ? details.attendees.map((attendee, index) => ({ attendee, index }))
    : details.attendees[attendeeIndex]
      ? [{ attendee: details.attendees[attendeeIndex], index: attendeeIndex }]
      : [];
  const transportationFields = (
    <>
      <div className="rsvp-extra__choices">
        <Choice
          type="radio"
          name="transportation-needed"
          label="כן"
          disabled={disabled}
          checked={details.transportation.answered && details.transportation.needsRide}
          onChange={() => updateDetails({
            ...details,
            transportation: { answered: true, needsRide: true, seats: Math.max(1, Number(details.transportation.seats || 0)) },
          })}
        />
        <Choice
          type="radio"
          name="transportation-needed"
          label="לא"
          disabled={disabled}
          checked={details.transportation.answered && !details.transportation.needsRide}
          onChange={() => updateDetails({ ...details, transportation: { answered: true, needsRide: false, seats: 0 } })}
        />
        {!options.transportation.required && details.transportation.answered && (
          <button
            type="button"
            className="rsvp-extra__clear"
            disabled={disabled}
            onClick={() => updateDetails({ ...details, transportation: { answered: false, needsRide: false, seats: 0 } })}
          >
            ניקוי בחירה
          </button>
        )}
      </div>
      {details.transportation.answered && details.transportation.needsRide && (
        <div className="rsvp-extra__seats">
          <span>כמה מקומות בהסעה?</span>
          <div className="rsvp-extra__stepper">
            <button
              type="button"
              disabled={disabled || details.transportation.seats >= partySize}
              onClick={() => updateDetails({
                ...details,
                transportation: { ...details.transportation, seats: Math.min(partySize, Number(details.transportation.seats || 0) + 1) },
              })}
            >+</button>
            <AnimatedNumber as="output" value={details.transportation.seats} ariaLabel="מספר מקומות בהסעה" />
            <button
              type="button"
              disabled={disabled || details.transportation.seats <= 1}
              onClick={() => updateDetails({
                ...details,
                transportation: { ...details.transportation, seats: Math.max(1, Number(details.transportation.seats || 1) - 1) },
              })}
            >−</button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <section className={`rsvp-extra ${compact ? "rsvp-extra--compact" : ""}`} aria-label="אפשרויות נוספות לאישור ההגעה">
      {showAttendees && aggregateAttendees && options.gender.enabled && (
        <details className="rsvp-dropdown" ref={genderDropdownRef}>
          <summary>
            <span className="rsvp-dropdown__summary-block">
              <span className="rsvp-dropdown__summary-line">
                <span className="rsvp-dropdown__title">חלוקת גברים / נשים</span>
                <span className="rsvp-dropdown__separator" aria-hidden="true">:</span>
              </span>
              {unassignedGenderCount > 0 ? (
                <span className="rsvp-dropdown__missing-status">
                  <span>טרם נבחרו: <AnimatedNumber as="span" value={unassignedGenderCount} /></span>
                  <span>יש לבחור לפני האישור</span>
                </span>
              ) : (
                <span className="rsvp-dropdown__status">גברים: <AnimatedNumber as="span" value={maleCount} /> · נשים: <AnimatedNumber as="span" value={femaleCount} /></span>
              )}
            </span>
            <b className="rsvp-dropdown__plus" aria-hidden="true">+</b>
          </summary>
          <div className="rsvp-dropdown__content rsvp-extra__block rsvp-aggregate">
            <div className="rsvp-aggregate__grid">
              <AggregateCounter
                label="גברים"
                value={maleCount}
                increaseDisabled={disabled || maleCount >= partySize}
                decreaseDisabled={disabled || maleCount <= 0}
                onIncrease={() => adjustGender("male", 1)}
                onDecrease={() => adjustGender("male", -1)}
              />
              <AggregateCounter
                label="נשים"
                value={femaleCount}
                increaseDisabled={disabled || femaleCount >= partySize}
                decreaseDisabled={disabled || femaleCount <= 0}
                onIncrease={() => adjustGender("female", 1)}
                onDecrease={() => adjustGender("female", -1)}
              />
            </div>
          </div>
        </details>
      )}

      {!aggregateAttendees && enabledFood.length > 0 && !alwaysShowFood && (
        <button
          type="button"
          className={`rsvp-extra__food-toggle ${foodOpen ? "rsvp-extra__food-toggle--open" : ""}`}
          aria-expanded={foodOpen}
          onClick={() => setFoodOpen((current) => !current)}
        >
          <span>{foodOpen ? "סגירת העדפות המזון" : "יש לי העדפות מזון"}</span>
          <b aria-hidden="true">⌄</b>
        </button>
      )}

      {showAttendees && aggregateAttendees && (options.childMeal.enabled || enabledFood.length > 0) && (
        <details className="rsvp-dropdown">
          <summary>
            <span className="rsvp-dropdown__title">העדפות אוכל ומנות</span>
            <b className="rsvp-dropdown__plus" aria-hidden="true">+</b>
          </summary>
          <div className="rsvp-dropdown__content rsvp-extra__block rsvp-aggregate rsvp-meals">
            <div className="rsvp-meals__groups">
              {mealGroups.map((group, index) => (
                <div
                  className={`rsvp-meals__group rsvp-meals__group--tone-${(index % 4) + 1} ${group.key === defaultMealKey ? "rsvp-meals__group--default" : ""}`}
                  key={group.key}
                >
                  <span>{getMealSummaryLabel(group)}</span>
                  {group.key === defaultMealKey ? (
                    <AnimatedNumber
                      value={group.attendeeIndexes.length}
                      className="rsvp-meals__regular-count"
                      ariaLabel={`כמות ${getMealSummaryLabel(group)}`}
                    />
                  ) : (
                    <div className="rsvp-extra__stepper">
                      <button
                        type="button"
                        disabled={disabled || !selectedMealGroups.get(defaultMealKey)?.attendeeIndexes.length}
                        onClick={() => adjustMealGroup(group, 1)}
                        aria-label={`הוספת ${getMealSummaryLabel(group)}`}
                      >+</button>
                      <AnimatedNumber as="output" value={group.attendeeIndexes.length} ariaLabel={`כמות ${getMealSummaryLabel(group)}`} />
                      <button
                        type="button"
                        disabled={disabled || group.attendeeIndexes.length <= 0}
                        onClick={() => adjustMealGroup(group, -1)}
                        aria-label={`הפחתת ${getMealSummaryLabel(group)}`}
                      >−</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </details>
      )}

      {showTransportation && options.transportation.enabled && (
        aggregateAttendees ? (
          <details className="rsvp-dropdown">
            <summary>
              <span className="rsvp-dropdown__summary-line">
                <span className="rsvp-dropdown__title">אפשרות הסעה</span>
                <span className="rsvp-dropdown__separator" aria-hidden="true">:</span>
                <span className="rsvp-dropdown__status">
                  {!details.transportation.answered
                    ? "ללא הסעה"
                    : details.transportation.needsRide
                      ? <>נדרשת הסעה: <AnimatedNumber as="span" value={details.transportation.seats} /> מקומות</>
                      : "ללא הסעה"}
                </span>
              </span>
              <b className="rsvp-dropdown__plus" aria-hidden="true">+</b>
            </summary>
            <div className="rsvp-dropdown__content rsvp-extra__block">
              <div className="rsvp-extra__heading">
                <small>{options.transportation.required ? "חובה לבחור" : "אפשר לענות לפי הצורך"}</small>
              </div>
              {transportationFields}
            </div>
          </details>
        ) : (
          <div className="rsvp-extra__block">
            <div className="rsvp-extra__heading">
              <strong>הסעה</strong>
              <small>{options.transportation.required ? "חובה לבחור" : "אפשר לענות לפי הצורך"}</small>
            </div>
            {transportationFields}
          </div>
        )
      )}

      {showAttendeeCards && (
        <div className="rsvp-extra__attendees">
          <div className="rsvp-extra__heading">
            <strong>פרטים לפי אורח</strong>
            <small>יש להשלים את אפשרויות החובה לכל אחד מהמגיעים</small>
          </div>
          {visibleAttendees.map(({ attendee, index }) => (
            <article className="rsvp-attendee" key={attendee.id || index}>
              <h4>אורח {index + 1}</h4>
              {options.childMeal.enabled && (
                <div className="rsvp-attendee__group">
                  <span>סוג מנה</span>
                  <div className="rsvp-extra__choices">
                    <Choice
                      type="radio"
                      name={`child-meal-${index}`}
                      label="מנות רגילות"
                      disabled={disabled}
                      checked={attendee.childMeal === false}
                      onChange={() => updateAttendee(index, { childMeal: false })}
                    />
                    <Choice
                      type="radio"
                      name={`child-meal-${index}`}
                      label="מנות ילדים"
                      disabled={disabled}
                      checked={attendee.childMeal === true}
                      onChange={() => updateAttendee(index, { childMeal: true })}
                    />
                  </div>
                </div>
              )}
              {options.gender.enabled && (
                <div className="rsvp-attendee__group">
                  <span>גבר / אישה</span>
                  <div className="rsvp-extra__choices">
                    <Choice
                      type="radio"
                      name={`gender-${index}`}
                      label="גבר"
                      disabled={disabled}
                      checked={attendee.gender === "male"}
                      onChange={() => updateAttendee(index, { gender: "male" })}
                    />
                    <Choice
                      type="radio"
                      name={`gender-${index}`}
                      label="אישה"
                      disabled={disabled}
                      checked={attendee.gender === "female"}
                      onChange={() => updateAttendee(index, { gender: "female" })}
                    />
                  </div>
                </div>
              )}
              {enabledFood.length > 0 && foodOpen && (
                <div className="rsvp-attendee__group">
                  <span>העדפות מזון</span>
                  <div className="rsvp-extra__choices rsvp-extra__choices--wrap">
                    <Choice
                      label="מנה רגילה"
                      disabled={disabled}
                      checked={
                        (regularMealIsDefault || (
                          attendee.foodAnswered === true &&
                          enabledFood.every((key) => attendee.answeredFoodOptions.includes(key))
                        )) &&
                        !enabledFood.some((key) => attendee[key] === true)
                      }
                      onChange={(event) => {
                        if (!event.target.checked) return;
                        onFoodInteraction?.();
                        updateAttendee(index, {
                          ...Object.fromEntries(enabledFood.map((key) => [key, false])),
                          foodAnswered: true,
                          answeredFoodOptions: Array.from(new Set([
                            ...attendee.answeredFoodOptions,
                            ...enabledFood,
                          ])),
                        });
                      }}
                    />
                    {enabledFood.map((key) => (
                      <Choice
                        key={key}
                        label={RSVP_OPTION_LABELS[key]}
                        disabled={disabled}
                        checked={attendee[key] === true}
                        onChange={(event) => {
                          onFoodInteraction?.();
                          const checked = event.target.checked;
                          const mutuallyExclusive = key === "vegetarian"
                            ? { vegan: false }
                            : key === "vegan"
                              ? { vegetarian: false }
                              : {};
                          updateAttendee(index, {
                            [key]: checked,
                            foodAnswered: true,
                            answeredFoodOptions: Array.from(new Set([
                              ...attendee.answeredFoodOptions,
                              ...enabledFood,
                            ])),
                            ...(checked ? mutuallyExclusive : {}),
                          });
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default RsvpAdditionalOptions;
